import { formatEuro } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { usePortalOptional } from '../frame/PortalLayout';
import type { PortalOverview, PortalOverviewBefore } from '../types';
import { PortalAction, countWord } from './actions';
import {
  AfterCard,
  DoneCard,
  NextStepCard,
  TodosCard,
  UnitsGate,
  VolumeCard,
  WaitingCard,
  type AdvisorLine,
} from './Cards';
import { Journey } from './Journey';
import { OwnerOverview } from './OwnerOverview';
import styles from './Overview.module.css';

/**
 * The sentence under „Ihr Weg zum Eigentum" of Portal 16 („Beratungstermin steht. Bis dahin sind zwei Unterlagen
 * fällig."), composed from the next appointment and the open requests (own).
 */
function todoSummary(data: PortalOverviewBefore): string {
  const next = data.journey.nextAppointment;
  const parts: string[] = [];
  if (next.kind === 'booked') parts.push(`${next.title} steht.`);
  else if (next.kind === 'pending') parts.push(`${next.title}.`);
  const count = data.todos.length;
  if (count === 1)
    parts.push(
      next.kind === 'booked' ? 'Bis dahin ist eine Unterlage fällig.' : 'Eine Unterlage ist fällig.',
    );
  else if (count > 1)
    parts.push(
      next.kind === 'booked'
        ? `Bis dahin sind ${countWord(count, false)} Unterlagen fällig.`
        : `${countWord(count, true)} Unterlagen sind fällig.`,
    );
  return parts.join(' ');
}

function BeforePurchase({
  data,
  preview,
  advisor,
}: {
  data: PortalOverviewBefore;
  preview: boolean;
  advisor: AdvisorLine | null;
}) {
  const firstRequest = data.todos[0]?.requestId;
  if (data.variant === 'advisor_todo') {
    return (
      <div className={styles.overview}>
        <Journey journey={data.journey} showStepLabel={false} preview={preview} />
        <div className={clsx(styles.split, styles.splitTight)}>
          {data.waiting ? <WaitingCard waiting={data.waiting} /> : null}
          {data.done.length > 0 ? <DoneCard done={data.done} /> : null}
        </div>
      </div>
    );
  }

  if (data.variant === 'customer_todo') {
    const next = data.journey.nextAppointment;
    const summary = todoSummary(data);
    return (
      <div className={styles.overview}>
        <div className={styles.todoHead}>
          <div>
            {data.volume ? (
              <div className={styles.eyebrow}>Ihr Finanzierungsvolumen · {formatEuro(data.volume.cents)}</div>
            ) : null}
            <h1 className={styles.todoTitle}>Ihr Weg zum Eigentum</h1>
            {summary ? <div className={styles.todoText}>{summary}</div> : null}
          </div>
          <div className={styles.buttons}>
            {next.kind === 'booked' && next.manageHref ? (
              <PortalAction target={{ href: next.manageHref }} preview={preview} variant="secondary">
                Termin verschieben
              </PortalAction>
            ) : null}
            <PortalAction target={{ section: 'questions' }} preview={preview}>
              {advisor ? `Frage an ${advisor.name}` : 'Frage stellen'}
            </PortalAction>
          </div>
        </div>
        <Journey journey={data.journey} showStepLabel={false} preview={preview} />
        {data.units ? <UnitsGate units={data.units} preview={preview} /> : null}
        <div className={clsx(styles.split, styles.splitTight)}>
          {data.todos.length > 0 ? <TodosCard todos={data.todos} preview={preview} /> : null}
          <AfterCard after={data.after} />
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(styles.overview, styles.gapFirst)}>
      {data.volume || data.nextStep ? (
        <div className={styles.split}>
          {data.volume ? <VolumeCard volume={data.volume} /> : null}
          {data.nextStep ? (
            <NextStepCard
              nextStep={data.nextStep}
              requestId={firstRequest}
              advisor={advisor}
              preview={preview}
            />
          ) : null}
        </div>
      ) : null}
      <Journey journey={data.journey} showStepLabel preview={preview} />
      {data.units ? <UnitsGate units={data.units} preview={preview} /> : null}
    </div>
  );
}

/**
 * The start page content of the client portal without frame (Mandantenportal 14 first login, 16 customer to-do,
 * 17 advisor to-do, 15 after the purchase) by `phase`/`variant`. Seam for F3: the staff preview „Portalansicht
 * öffnen" mounts it with `GET /contacts/:id/portal/overview` and `preview` — every action then renders disabled.
 * Inside the portal frame the advisor and organization come from `GET /api/portal/me`; the preview omits the
 * advisor row.
 */
export function OverviewContent({ data, preview }: { data: PortalOverview; preview?: boolean }) {
  const portal = usePortalOptional();
  const inPreview = preview === true || portal === null;
  if (data.phase === 'after_purchase') return <OwnerOverview data={data} preview={inPreview} />;
  const advisor: AdvisorLine | null =
    portal && portal.me.advisor !== null
      ? {
          name: portal.me.advisor.name,
          initials: portal.me.advisor.initials,
          phone: portal.me.advisor.phone,
          organizationName: portal.me.organization.name,
        }
      : null;
  return <BeforePurchase data={data} preview={inPreview} advisor={advisor} />;
}
